import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Pill } from "./Pill";
import { SourceBadge } from "./SourceBadge";
import { api, formatDateIST } from "../lib/api";
import { useLookups } from "../lib/lookups";
import { Mail, Phone, MessageCircle, ExternalLink, Calendar, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import ActivityTimeline from "./ActivityTimeline";

const SMART = {
  "New Enquiry": 1,
  Contacted: 2,
  Followup: 3,
  "Demo Scheduled": 1,
  "Demo Done": 2,
  "Fees Shared": 2,
};

export function LeadDetail({ lead, open, onClose, onUpdated }) {
  const { CATEGORIES, STAGES, CATEGORY_COLORS, STAGE_COLORS } = useLookups();
  const [editing, setEditing] = useState(false);
  const [summary, setSummary] = useState(lead?.summary || "");
  const [budget, setBudget] = useState(lead?.budget || "");
  const [name, setName] = useState(lead?.name || "");
  const [editingName, setEditingName] = useState(false);
  const [reminderWhen, setReminderWhen] = useState("");
  const [reminderNote, setReminderNote] = useState("");

  React.useEffect(() => {
    setSummary(lead?.summary || "");
    setBudget(lead?.budget || "");
    setName(lead?.name || "");
    setEditingName(false);
    setReminderWhen("");
    setReminderNote("");
  }, [lead]);

  if (!lead) return null;

  const patch = async (payload) => {
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, payload);
      onUpdated && onUpdated(data);
      toast.success("Updated");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Name cannot be empty");
      setName(lead.name);
      setEditingName(false);
      return;
    }
    if (trimmed === lead.name) {
      setEditingName(false);
      return;
    }
    await patch({ name: trimmed });
    setEditingName(false);
  };

  const setReminder = async () => {
    if (!reminderWhen) return toast.error("Pick a date/time");
    const iso = new Date(reminderWhen).toISOString();
    const { data } = await api.post(`/leads/${lead.id}/reminder`, { when: iso, note: reminderNote });
    onUpdated && onUpdated(data);
    setReminderWhen("");
    setReminderNote("");
    toast.success("Reminder set");
  };

  const markDone = async () => {
    await api.post(`/leads/${lead.id}/reminder/done`);
    const { data } = await api.get(`/leads/${lead.id}`);
    onUpdated && onUpdated(data);
    toast.success("Follow-up done");
  };

  const snooze = async (days) => {
    await api.post(`/leads/${lead.id}/reminder/snooze`, { days });
    const { data } = await api.get(`/leads/${lead.id}`);
    onUpdated && onUpdated(data);
    toast.success(`Snoozed +${days}d`);
  };

  const applySmart = () => {
    const d = SMART[lead.stage];
    if (!d) return;
    const dt = new Date();
    dt.setDate(dt.getDate() + d);
    dt.setHours(10, 0, 0, 0);
    const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setReminderWhen(local);
  };

  const showAttr =
    lead.campaign_name || lead.adset_name || lead.ad_name || lead.keyword || lead.source_channel || lead.referred_by;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white" data-testid="lead-detail-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-[540] flex items-center gap-3 flex-wrap">
            {editingName ? (
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={saveName}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); saveName(); }
                  if (e.key === "Escape") { setName(lead.name); setEditingName(false); }
                }}
                data-testid="lead-name-input"
                className="rounded-[6px] border border-[#6C47FF] px-2 py-1 text-xl font-[540] bg-white min-w-[240px] focus:outline-none focus:border-[#6C47FF]"
              />
            ) : (
              <button
                onClick={() => setEditingName(true)}
                data-testid="lead-name-display"
                title="Click to edit name"
                className="text-left hover:underline decoration-dotted underline-offset-4"
              >
                {lead.name}
              </button>
            )}
            <Pill color={CATEGORY_COLORS[lead.category]}>{lead.category}</Pill>
            <Pill color={STAGE_COLORS[lead.stage]}>{lead.stage}</Pill>
          </DialogTitle>
          {lead.created_at && (
            <div className="text-xs text-[#6B7280] mt-1" data-testid="lead-created-at">
              Created on {(() => {
                try {
                  const d = new Date(lead.created_at);
                  const dateStr = new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Kolkata", month: "short", day: "numeric", year: "numeric" }).format(d);
                  const timeStr = new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Kolkata", hour: "numeric", minute: "2-digit", hour12: true }).format(d).replace(" AM", " AM").replace(" PM", " PM");
                  return `${dateStr} at ${timeStr}`;
                } catch { return lead.created_at; }
              })()}
            </div>
          )}
        </DialogHeader>

        <div className="space-y-6">
          {/* IDENTITY */}
          <Section title="Identity">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              {lead.email && (
                <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-2 text-[#6C47FF] hover:underline" data-testid="lead-email-link">
                  <Mail className="h-4 w-4" /> {lead.email}
                </a>
              )}
              {lead.phone && (
                <>
                  <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-2 text-[#6C47FF] hover:underline" data-testid="lead-phone-link">
                    <Phone className="h-4 w-4" /> {lead.phone}
                  </a>
                  <a
                    href={`https://wa.me/${lead.phone.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-[#166534] hover:underline"
                    data-testid="lead-whatsapp-link"
                  >
                    <MessageCircle className="h-4 w-4" /> WhatsApp
                  </a>
                </>
              )}
              {lead.inbox_url && (
                <a
                  href={lead.inbox_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[8px] border border-[#6C47FF] text-sm"
                  data-testid="lead-meta-conversation"
                >
                  <ExternalLink className="h-4 w-4" /> Open Meta Conversation
                </a>
              )}
            </div>
          </Section>

          {/* SOURCE & ATTRIBUTION */}
          {showAttr && (
            <Section title="Where this lead came from">
              <div className="flex items-center gap-2 flex-wrap">
                <SourceBadge source={lead.source} />
                <span className="text-xs px-2 py-1 rounded-full bg-[#F0F1F7] border border-[#E8EAEF] text-[#1A1A2E] font-[600]">
                  {lead.is_organic ? "Organic" : "Paid"}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mt-3">
                {lead.campaign_name && <Field label="Campaign" value={lead.campaign_name} />}
                {lead.adset_name && <Field label="Ad Set" value={lead.adset_name} />}
                {lead.ad_name && <Field label="Ad" value={lead.ad_name} />}
                {lead.keyword && <Field label="Keyword" value={lead.keyword} />}
                {lead.source_channel && <Field label="Channel" value={lead.source_channel} />}
                {lead.referred_by && <Field label="Referred By" value={lead.referred_by} />}
              </div>
            </Section>
          )}

          {/* FORM ANSWERS */}
          <Section title="Form Answers">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <Field label="Course Interest" value={lead.course_interest || "—"} />
              <Field label="Education Level" value={lead.education_level || "—"} />
              <Field label="Career Goal" value={lead.career_goal || "—"} />
              <Field label="Budget" value={lead.budget || "—"} />
            </div>
          </Section>

          {/* TAGS */}
          <Section title="Tags">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#6B7280]">Category</label>
                <select
                  value={lead.category}
                  onChange={(e) => patch({ category: e.target.value })}
                  data-testid="lead-category-select"
                  className="w-full mt-1 rounded-[6px] border border-[#E8EAEF] px-3 py-2 min-h-[44px] text-sm bg-white"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#6B7280]">Stage</label>
                <select
                  value={lead.stage}
                  onChange={(e) => patch({ stage: e.target.value })}
                  data-testid="lead-stage-select"
                  className="w-full mt-1 rounded-[6px] border border-[#E8EAEF] px-3 py-2 min-h-[44px] text-sm bg-white"
                >
                  {STAGES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
          </Section>

          {/* FIELDS */}
          <Section title="Fields">
            <label className="text-xs text-[#6B7280]">Summary / Note</label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              onBlur={() => summary !== lead.summary && patch({ summary })}
              rows={3}
              data-testid="lead-summary-textarea"
              className="w-full mt-1 rounded-[6px] border border-[#E8EAEF] px-3 py-2 text-sm bg-white"
            />
            <label className="text-xs text-[#6B7280] mt-3 block">Budget</label>
            <input
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              onBlur={() => budget !== lead.budget && patch({ budget })}
              data-testid="lead-budget-input"
              className="w-full mt-1 rounded-[6px] border border-[#E8EAEF] px-3 py-2 min-h-[44px] text-sm bg-white"
            />
          </Section>

          {/* REMINDERS */}
          <Section title="Follow-up Reminder">
            {lead.next_followup_at ? (
              <div className="flex items-center justify-between bg-[#F0F1F7] border border-[#E8EAEF] rounded-[8px] p-3" data-testid="active-reminder">
                <div>
                  <div className="text-sm font-[600] flex items-center gap-2">
                    <Calendar className="h-4 w-4" /> {formatDateIST(lead.next_followup_at)}
                  </div>
                  {lead.followup_note && <div className="text-xs text-[#6B7280] mt-1">{lead.followup_note}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={markDone} data-testid="reminder-done-btn" className="px-3 py-1.5 rounded-[8px] bg-[#166534] text-white text-xs font-[600] min-h-[36px]">
                    <Check className="h-3 w-3 inline mr-1" /> Done
                  </button>
                  <button onClick={() => snooze(1)} data-testid="reminder-snooze-1-btn" className="px-3 py-1.5 rounded-[8px] border border-[#6C47FF] text-xs font-[600] min-h-[36px]">
                    +1d
                  </button>
                  <button onClick={() => snooze(3)} data-testid="reminder-snooze-3-btn" className="px-3 py-1.5 rounded-[8px] border border-[#6C47FF] text-xs font-[600] min-h-[36px]">
                    +3d
                  </button>
                </div>
              </div>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
              <input
                type="datetime-local"
                value={reminderWhen}
                onChange={(e) => setReminderWhen(e.target.value)}
                data-testid="reminder-when"
                className="rounded-[6px] border border-[#E8EAEF] px-3 py-2 min-h-[44px] text-sm bg-white"
              />
              <input
                value={reminderNote}
                onChange={(e) => setReminderNote(e.target.value)}
                placeholder="Note"
                data-testid="reminder-note"
                className="rounded-[6px] border border-[#E8EAEF] px-3 py-2 min-h-[44px] text-sm bg-white"
              />
              <div className="flex gap-2">
                <button onClick={setReminder} data-testid="reminder-set-btn" className="flex-1 btn-mx rounded-[8px] min-h-[44px] text-sm font-[600]">
                  Set Reminder
                </button>
                {SMART[lead.stage] && (
                  <button onClick={applySmart} title={`Smart: +${SMART[lead.stage]}d`} className="px-3 rounded-[8px] border border-[#6C47FF] text-xs font-[600] min-h-[44px]">
                    <Clock className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          </Section>

          {/* ACTIVITY */}
          <ActivityTimeline lead={lead} onUpdated={onUpdated} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-xs font-[600] uppercase tracking-wider text-[#6B7280] mb-3 border-b border-[#E8EAEF] pb-2">{title}</h3>
      <div>{children}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs text-[#6B7280]">{label}</div>
      <div className="text-sm text-[#1A1A2E] font-[460] mt-0.5">{value}</div>
    </div>
  );
}
